import {useEffect, useState} from 'react'

/**
 * Custom hook to get the natural size of an image.
 * @param {HTMLImageElement} imgElement - The image element to observe.
 * @returns {null|{width: number, height: number}} - The natural size of the image or null if not available.
 */
const useImgNaturalSize = (imgElement) => {
    const [naturalSize, setNaturalSize] = useState(null)
    useEffect(() => {
        // clear the previous natural size if any when imgElement changes
        setNaturalSize(null)

        // return early if imgElement is not provided
        if (!imgElement) {
            return
        }

        // handle the image load event
        const handleImageLoad = () => {
            if (imgElement.naturalWidth !== 0 && imgElement.naturalHeight !== 0) {
                setNaturalSize((currentSize) =>
                    // Check if the current size is the same and do not update to prevent unnecessary re-renders
                    currentSize?.width === imgElement.naturalWidth &&
                    currentSize?.height === imgElement.naturalHeight
                        ? currentSize
                        : {width: imgElement.naturalWidth, height: imgElement.naturalHeight}
                )
            } else {
                // reset the natural size if the image is not loaded properly or has zero dimensions
                // (handles broken images and images that are loading a new src)
                setNaturalSize(null)
            }
        }

        const handleImageError = () => {
            // reset the natural size if the image fails to load
            setNaturalSize(null)
        }

        // `complete` is updated async during execution of the script, so we first register the event listener
        // and then check if the image is already loaded to cover cases where the image is already loaded,
        // loads after the script runs, or in between registering the event listener and checking if it's loaded.

        // First register the event listener
        imgElement.addEventListener('load', handleImageLoad)
        imgElement.addEventListener('error', handleImageError)

        // Then check if image is already loaded (or failed to load)
        if (imgElement.complete) {
            handleImageLoad() // This handles both successful loads and broken images (zero dimensions)
            // do not remove the event listeners in case the image changes later
        }

        return () => {
            imgElement.removeEventListener('load', handleImageLoad)
            imgElement.removeEventListener('error', handleImageError)
        }
    }, [imgElement])

    return naturalSize
}

export default useImgNaturalSize
